import { isEmpty } from 'lodash';
import {
    makeApiPostRequest,
    makeRetriableGetRequest,
    makeBulkGetRequest, getPostConfig,
} from './apiActions';
import TROServiceClient from '../clients/TROServiceClient';
import { setResult } from './resultActions';
import selectors from '../../rootSelectors';
import { TRO, AR_SMALL } from '../../constants/measure';
import { setNextResumeResultItem } from '../resume/resumeResultActions';
import { startBackgroundRetry, stopBackgroundRetry, reportAppError, apiCallSucceeded } from './apiCallActions';
import { VERIFICATION_STATUS } from '../../trc/pages/instructions/TROInstructionsConstants';
import { hideSpinner, showSpinner } from '../screen/screenActions';
import { RESULT_NOT_FOUND_ERROR, VR_RESULT_STATUS } from '../../constants/error';

const measure = TRO.MEASURE;
const assessment = TRO.ASSESSMENT;

export const SET_VERIFICATION_STATUS = 'SET_VERIFICATION_STATUS';
export const SET_VERIFICATION_RESULT = 'SET_VERIFICATION_RESULT';
export const EXECUTE_API_CALL_SUCCEEDED_EFFECT = 'EXECUTE_API_CALL_SUCCEEDED_EFFECT';
export const EXECUTE_API_CALL_FAILED_EFFECT = 'EXECUTE_API_CALL_FAILED_EFFECT';

export const setVerificationStatus = status => ({
    type: SET_VERIFICATION_STATUS,
    payload: status,
});

export const setVerificationResult = result => ({
    type: SET_VERIFICATION_RESULT,
    payload: result,
});

export const executeApiCallSucceededEffect = payload => ({
    type: EXECUTE_API_CALL_SUCCEEDED_EFFECT,
    payload
});

export const executeApiCallFailedEffect = payload => ({
    type: EXECUTE_API_CALL_FAILED_EFFECT,
    payload
});

export const createResult = () => (dispatch, getStore) => {
    const state = getStore();
    const instId = selectors.getInstId(state, measure);
    const postBody = {
        assessment: selectors.getExternalURLSourceStatus(state) ? AR_SMALL : TROServiceClient.NAME,
        measure,
        assessment_mode: 'online',
        grade_level: selectors.getGrade(state),
        inst_uid: instId,
        period: selectors.getToy(state),
        student_uid: selectors.getUserId(state),
        school_year: selectors.getSchoolYear(state),
        // TODO: replace `|| selectors.getUserId(state)` with `|| undefined` as in other actions
        // after TRO supports results without `student_temporal_id`
        student_temporal_id: selectors.getStudentTemporalId(state, instId) || selectors.getUserId(state)
    };
    const endpoint = TROServiceClient.constructResultListEndpoint({ path: TROServiceClient.TRO_RESULTS_URL });
    return dispatch(makeApiPostRequest(endpoint, postBody))
        .then((resultJson) => {
            dispatch(setResult(resultJson));
            return resultJson.result_id;
        });
};

const updateBookResult = (operation, data, resultId) => (dispatch, getStore) => {
    const state = getStore();
    const studentUid = selectors.getUserId(state);
    const instId = selectors.getInstId(state, measure);

    return dispatch(TROServiceClient.patchResult(resultId, operation, data, studentUid, instId))
        .then((response) => ({
            ...response,
            resultId,
        }));
};

export const addBookResult = (resultId) => (dispatch, getStore) => {
    const state = getStore();
    const bookId = selectors.getTRCBookId(state);
    const relativeLevel = selectors.getTRCBookRelativeLevel(state);
    const response = {
        book_id: bookId,
        book_relative_level: relativeLevel,
    };
    return dispatch(updateBookResult(
        TROServiceClient.RESULT_OPERATION.ADD_BOOK_RESULT, response, resultId,
    ))
        .then((data) => {
            dispatch(setNextResumeResultItem(data.resultId));
            return data;
        });
};

const createCurrentResultId = () => (dispatch, getStore) => {
    const state = getStore();
    const result = selectors.findResultFor(state, { measure });
    if (!result) {
        return dispatch(createResult()).then(data => {
            return data.resultId;
        });
    }
    return Promise.resolve(result.id);
};

export const saveComprehensionResponse = (response, isLast) => dispatch => {
    return dispatch(createCurrentResultId())
        .then(resultId => dispatch(updateBookResult(
            TROServiceClient.RESULT_OPERATION.ADD_COMPREHENSION_RESPONSE,
            response,
            resultId
        ))).then(data => { if (isLast) dispatch(setNextResumeResultItem(data.resultId)); });
};


export const saveTimeDuration = (data, resultId) => dispatch => dispatch(updateBookResult(
    TROServiceClient.RESULT_OPERATION.ADD_BOOK_STATS,
    data,
    resultId,
));

export const getBook = (config, retryActionName) => (dispatch) => {
    const endpoint = TROServiceClient.constructBookEndpoint(config);
    return dispatch(makeRetriableGetRequest(endpoint, retryActionName))
        .then((contentJson) => {
            if (contentJson) {
                return contentJson;
            }
            return Promise.reject(contentJson);
        });
};

export const getBookImages = () => (dispatch, getStore) => {
    const state = getStore();
    const imageURLs = selectors.getTRCImageURLs(state);
    return dispatch(makeBulkGetRequest(imageURLs));
};

export const getStudentResults = (shouldSetResult = true) => (dispatch, getStore) => {
    const state = getStore();
    const studentId = selectors.getUserId(state);
    const grade = selectors.getGrade(state);
    const period = selectors.getToy(state);
    const schoolYear = selectors.getSchoolYear(state);
    const instId = selectors.getInstId(state, measure);

    return dispatch(TROServiceClient.getResults(studentId, grade, period, schoolYear, instId))
        .then((data) => {
            const resultsData = isEmpty(data) ? [] : data;
            if (shouldSetResult) resultsData.forEach(resultData => dispatch(setResult(resultData)));
            return data;
        });
};

export const saveStudentRecording = () => async (dispatch, getStore) => {
    const state = getStore();
    const resultId = selectors.findResultFor(state, { measure, isComplete: false }).id;
    const bookId = selectors.getTRCBookId(state);

    if (window.amplify_lantern_config.isVRTestMode && !window.d394cd2151.ab934dff) {
        // pretend we sent audio request and it succeded
        dispatch(apiCallSucceeded(`/tro_results_api/results/${resultId}/${bookId}/student_voice_recording`, getPostConfig({})));
        return Promise.resolve();
    }
    let encodedWav;
    try {
        encodedWav = await selectors.getEncodedWavResult(state);
    } catch (error) {
        dispatch(reportAppError(error.message));
        throw error;
    }
    const lastWordIndex = selectors.getLastWordIndex(state);
    const payload = {
        studentUid: selectors.getUserId(state),
        encodedWav,
        resultId,
        bookId,
        lastWordIndex
    };
    return dispatch(TROServiceClient.postRecording(payload));
};

export const saveStudentRecordingWithRetry = () => async (dispatch) => {
    dispatch(startBackgroundRetry());
    return dispatch(saveStudentRecording())
        .finally(() => {
            dispatch(stopBackgroundRetry());
        });
};

export const getResultWithRetry = (resultId) => (dispatch, getStore) => {
    const state = getStore();
    const studentId = selectors.getUserId(state);
    const grade = selectors.getGrade(state);
    const period = selectors.getToy(state);
    const schoolYear = selectors.getSchoolYear(state);

    return dispatch(TROServiceClient.getReadyResultWithRetry(studentId, grade, period, schoolYear, resultId))
        .then(data => {
            const currentResult = data.find(result => result.result_id === resultId);
            if (!currentResult) return Promise.reject(RESULT_NOT_FOUND_ERROR);
            return currentResult;
        });
};

export const getVerificationResult = () => async (dispatch, getStore) => {
    const state = getStore();

    const studentDetails = {
        student_uid: selectors.getUserId(state).toString(),
        period: selectors.getToy(state),
        school_year: selectors.getSchoolYear(state).toString(),
        inst_uid: selectors.getInstId(state, measure).toString(),
    };

    let encodedWav;
    try {
        encodedWav = await selectors.getEncodedWavResult(state);
    } catch (error) {
        dispatch(reportAppError(error.message));
        throw error;
    }

    const target = 'The dog likes to play with the ball';
    const verificationType = 'mic_check';

    const payload = {
        studentDetails,
        target,
        encodedWav,
        verificationType,
    };

    dispatch(setVerificationStatus(VERIFICATION_STATUS.PENDING));
    dispatch(showSpinner());

    dispatch(TROServiceClient.postVerification(payload))
        .then(data => {
            dispatch(setVerificationResult(data));
            dispatch(setVerificationStatus(VERIFICATION_STATUS.COMPLETE));
        }
        )
        .catch(() => {
            dispatch(setVerificationStatus(VERIFICATION_STATUS.ERROR));
        })
        .finally(() => {
            dispatch(hideSpinner());
        });
};

export const setStudentResultToResume = () => (dispatch, getStore) => {
    const state = getStore();
    const result = selectors.findResultFor(state, { measure, isComplete: false });
    const bookResults = selectors.getTRCBookResults(state);

    const lastIndex = bookResults.length - 1;
    const resumeData = {
        ...bookResults[lastIndex],
        is_valid: true,
        vrr_result: {
            status: VR_RESULT_STATUS.UNAVAILABLE
        }
    };

    const measureResults = [...bookResults];
    measureResults[lastIndex] = resumeData;

    if (result) {

        const resultId = result.id;

        const resultData = {
            is_valid: true,
            is_complete: false,
            result_id: resultId,
            measure_result: {
                book_results: measureResults,
            },
            assessment: selectors.getExternalURLSourceStatus(state) ? AR_SMALL : assessment,
            measure,
            assessment_mode: "online",
            school_year: selectors.getSchoolYear(state),
            inst_uid: selectors.getInstId(state, measure),
            period: selectors.getToy(state),
            grade_level: selectors.getGrade(state)
        };

        dispatch(setResult(resultData));
    }
};
